import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { CaptureToolbar, type StagedCapture } from "./CaptureToolbar";
import { fileNames } from "./captureFileName";
import { useT } from "../../shared/i18n";

/**
 * Photo-only capture control that stages photos until the parent commits (the
 * item-create dialogs stage covers, then upload after the item exists). Shows
 * the count of photos awaiting the save so the user isn't surprised.
 */
export function StagedPhotoStrip({
  captures,
  onChange,
}: {
  captures: StagedCapture[];
  onChange: (captures: StagedCapture[]) => void;
}) {
  const t = useT();

  const add = (capture: StagedCapture) => onChange([...captures, capture]);
  const remove = (index: number) => onChange(captures.filter((_, i) => i !== index));

  return (
    <Stack spacing={1}>
      <CaptureToolbar onStaged={add} photoOnly usedNames={fileNames(captures)} />
      {captures.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {captures.map((c, i) => (
            <Chip
              key={`${c.file.name}-${i}`}
              label={c.file.name}
              size="small"
              onDelete={() => remove(i)}
            />
          ))}
        </Stack>
      )}
      <Typography variant="caption" color="text.secondary">
        {captures.length === 0
          ? t("stagedPhotoHint")
          : captures.length === 1
            ? t("stagedPhotoCountOne", { count: 1 })
            : t("stagedPhotoCount", { count: captures.length })}
      </Typography>
    </Stack>
  );
}